// User types from the backend
export const USER_TYPES = {
  PATIENT: 'patient',
  PRACTICE_OWNER: 'practiceOwner',
  DOCTOR: 'doctor',
};

// Permission definitions
export const PERMISSIONS = {
  // Dashboard access
  VIEW_DASHBOARD: 'view_dashboard',
  VIEW_PRACTICE_METRICS: 'view_practice_metrics',
  
  // Patient management
  VIEW_PATIENTS: 'view_patients',
  MANAGE_PATIENTS: 'manage_patients',
  VIEW_PATIENT_PORTAL: 'view_patient_portal',
  
  // Medical data
  VIEW_BLOOD_TESTS: 'view_blood_tests',
  MANAGE_BLOOD_TESTS: 'manage_blood_tests',
  VIEW_BIOLOGICAL_AGE: 'view_biological_age',
  VIEW_CLINICAL_NOTES: 'view_clinical_notes',
  MANAGE_CLINICAL_NOTES: 'manage_clinical_notes',
  
  // Services and appointments
  VIEW_SERVICES: 'view_services',
  MANAGE_SERVICES: 'manage_services',
  VIEW_CALENDAR: 'view_calendar',
  MANAGE_CALENDAR: 'manage_calendar',
  
  // Prescriptions
  VIEW_PRESCRIPTIONS: 'view_prescriptions',
  MANAGE_PRESCRIPTIONS: 'manage_prescriptions',
  
  // Analytics and reports
  VIEW_ANALYTICS: 'view_analytics',
  VIEW_REPORTS: 'view_reports',
  
  // Testing services
  VIEW_CONCIERGE_TESTING: 'view_concierge_testing',
  MANAGE_CONCIERGE_TESTING: 'manage_concierge_testing',
  
  // Inventory and billing
  VIEW_INVENTORY: 'view_inventory',
  MANAGE_INVENTORY: 'manage_inventory',
  VIEW_BILLING: 'view_billing',
  MANAGE_BILLING: 'manage_billing',
  
  // Wearables
  VIEW_WEARABLES: 'view_wearables',
  MANAGE_WEARABLES: 'manage_wearables',
  
  // Settings and admin
  VIEW_SETTINGS: 'view_settings',
  MANAGE_SETTINGS: 'manage_settings',
  MANAGE_USERS: 'manage_users',
  UPLOAD_DATA: 'upload_data',
};

// Role definitions with their permissions
export const ROLES = {
  [USER_TYPES.PATIENT]: {
    name: 'Patient',
    permissions: [
      PERMISSIONS.VIEW_PATIENT_PORTAL,
      PERMISSIONS.VIEW_BLOOD_TESTS,
      PERMISSIONS.VIEW_BIOLOGICAL_AGE,
      PERMISSIONS.VIEW_PRESCRIPTIONS,
      PERMISSIONS.VIEW_CALENDAR,
      PERMISSIONS.VIEW_WEARABLES,
      PERMISSIONS.VIEW_SETTINGS,
    ],
  },
  
  [USER_TYPES.DOCTOR]: {
    name: 'Doctor',
    permissions: [
      PERMISSIONS.VIEW_DASHBOARD,
      PERMISSIONS.VIEW_PATIENTS,
      PERMISSIONS.MANAGE_PATIENTS,
      PERMISSIONS.VIEW_PATIENT_PORTAL,
      PERMISSIONS.VIEW_BLOOD_TESTS,
      PERMISSIONS.MANAGE_BLOOD_TESTS,
      PERMISSIONS.VIEW_BIOLOGICAL_AGE,
      PERMISSIONS.VIEW_CLINICAL_NOTES,
      PERMISSIONS.MANAGE_CLINICAL_NOTES,
      PERMISSIONS.VIEW_SERVICES,
      PERMISSIONS.VIEW_CALENDAR,
      PERMISSIONS.MANAGE_CALENDAR,
      PERMISSIONS.VIEW_PRESCRIPTIONS,
      PERMISSIONS.MANAGE_PRESCRIPTIONS,
      PERMISSIONS.VIEW_ANALYTICS,
      PERMISSIONS.VIEW_REPORTS,
      PERMISSIONS.VIEW_CONCIERGE_TESTING,
      PERMISSIONS.MANAGE_CONCIERGE_TESTING,
      PERMISSIONS.VIEW_WEARABLES,
      PERMISSIONS.MANAGE_WEARABLES,
      PERMISSIONS.VIEW_SETTINGS,
      PERMISSIONS.UPLOAD_DATA,
    ],
  },
  
  [USER_TYPES.PRACTICE_OWNER]: {
    name: 'Practice Owner',
    permissions: [
      // All permissions - practice owners have full access
      ...Object.values(PERMISSIONS),
    ],
  },
};

// Helper functions
export const getUserRole = (user) => {
  if (!user) return null;
  
  // Check if user is practice owner
  if (user.isOwner || user.userType === USER_TYPES.PRACTICE_OWNER) {
    return USER_TYPES.PRACTICE_OWNER;
  }
  
  // Check for explicit userType
  if (user.userType) {
    return user.userType;
  }
  
  // Default to patient if no specific type is set
  return USER_TYPES.PATIENT;
};

export const getUserPermissions = (user) => {
  const role = getUserRole(user);
  return ROLES[role]?.permissions || [];
};

export const hasPermission = (user, permission) => {
  const permissions = getUserPermissions(user);
  return permissions.includes(permission);
};

export const hasAnyPermission = (user, permissionList) => {
  const permissions = getUserPermissions(user);
  return permissionList.some(permission => permissions.includes(permission));
};

export const hasAllPermissions = (user, permissionList) => {
  const permissions = getUserPermissions(user);
  return permissionList.every(permission => permissions.includes(permission));
};

// Navigation item visibility rules
export const getVisibleNavigationItems = (user, navigationItems) => {
  const role = getUserRole(user);
  
  // Define which navigation items are visible for each role
  const roleNavigationMap = {
    [USER_TYPES.PATIENT]: [
      'Patient Portal',
      'Blood Tests',
      'Biological Age',
      'Prescriptions',
      'Calendar',
      'Wearables',
      'Settings',
    ],
    
    [USER_TYPES.DOCTOR]: [
      'Dashboard',
      'Patient Portal',
      'Blood Tests',
      'Biological Age',
      'Concierge Testing',
      'Analytics',
      'Reports',
      'Clinical Notes',
      'Services',
      'Prescriptions',
      'Calendar',
      'Wearables',
      'Settings',
      'Upload Data',
    ],
    
    [USER_TYPES.PRACTICE_OWNER]: [
      // All navigation items - practice owners see everything
      'Dashboard',
      'Patient Portal',
      'Blood Tests',
      'Biological Age',
      'Concierge Testing',
      'Analytics',
      'Reports',
      'Clinical Notes',
      'Services',
      'Prescriptions',
      'Calendar',
      'Inventory',
      'Billing',
      'Wearables',
      'Settings',
      'Upload Data',
    ],
  };
  
  const allowedItems = roleNavigationMap[role] || [];
  return navigationItems.filter(item => allowedItems.includes(item.title));
};